<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { Deposit } from '~/schemas/deposits'

const { t } = useI18n()
const dialogRef = inject('dialogRef') as any
const shiftId = computed(() => dialogRef?.value?.data?.shiftId)
const depositsStore = useDepositsStore()
// Fund sources are owned by the expense store; reuse it instead of
// duplicating the fetch (deposits have no expense-type equivalent catalog).
const expenseStore = useExpenseStore()
const { fundSources } = storeToRefs(expenseStore)

const fundSourceId = ref<number | null>(null)
const fundSourceError = ref('')

watch(fundSourceId, (v) => {
  if (v) fundSourceError.value = ''
})

async function depositSubmit(formData: Deposit): Promise<void> {
  if (!fundSourceId.value) {
    fundSourceError.value = t('Fund source is required')
    return
  }
  fundSourceError.value = ''

  const success = await depositsStore.createDeposit({
    ...formData,
    shift_id: shiftId.value ?? null,
    fund_source_id: fundSourceId.value,
  })
  if (success) {
    dialogRef.value?.close()
  }
}

onMounted(() => {
  expenseStore.fetchFundSources()
})
</script>

<template>
  
<FormKitLazyProvider config-file="true">
<FormKit
    id="new-deposit"
    class="flex"
    type="form"
    :actions="false"
    @submit="depositSubmit"
  >
    <div class="flex gap-3 justify-center">
      <div class="w-1/2">
        <FormKit
          v-if="shiftId"
          type="text"
          :label="t('Shift ID')"
          number
          placeholder="0"
          :disabled="true"
          :value="shiftId"
          name="shift_id"
        />
        <div class="mb-3">
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('Fund source') }}</label>
          <FormFundSourceSelect
            v-model="fundSourceId"
            :options="fundSources"
            :allow-create="false"
          />
          <small
            v-if="fundSourceError"
            class="text-red-500"
          >{{ fundSourceError }}</small>
        </div>
        <FormKit
          type="text"
          :label="t('Amount')"
          placeholder="0.00"
          validation="required|number"
          validation-visibility="live"
          name="amount"
        />
        <FormKit
          type="textarea"
          cols="20"
          :label="t('Notes')"
          name="notes"
        />
        <FormKit
          type="submit"
          :label="t('Add deposit')"
        />
      </div>
    </div>
  </FormKit>
</FormKitLazyProvider>

</template>
